import React from "react";
import cx from "classnames";
import { useRouter } from "next/router";
import st from "./sliderTextButton.module.scss";
import { Button } from "@pepdirect/ui/button";
import { useScreenSize } from "@pepdirect/helpers/useScreenSize";
import { InternalPromotionalAnalyticRecord } from "services/datocms/generated";
import { logInternalPromotionSelect } from "@pepdirect/helpers/analyticsLogger";
import { Product } from "@pepdirect/shared/types";

type Props = {
  subHeader: string;
  header: string;
  textColor: string;
  disclaimer?: string;
  isCenter?: boolean;
  primaryButton: {
    text: string;
    color: string;
    textColor: string;
    ctaUrl: string;
  };
  secondaryButton?: {
    text: string;
    color: string;
    textColor: string;
    ctaUrl: string;
  };
  internalPromotionAnalytics?: InternalPromotionalAnalyticRecord;
  primaryItem?: Product;
  secondaryItem?: Product;
};

const SliderTextButton = (props: Props) => {
  const router = useRouter();
  const { isMobile } = useScreenSize();
  const {
    subHeader,
    header,
    disclaimer,
    primaryButton,
    secondaryButton,
    isCenter,
    internalPromotionAnalytics,
    primaryItem,
    secondaryItem,
  } = props;

  const headingStyle = { color: props.textColor };
  const primaryButtonStyle = {
    backgroundColor: primaryButton.color,
    color: primaryButton.textColor,
  };
  const secondaryButtonStyle = secondaryButton
    ? { borderColor: secondaryButton.color, color: secondaryButton.textColor }
    : {};

  const onButtonClick = (ctaUrl: string, isPrimaryButton?: boolean) => {
    internalPromotionAnalytics &&
      logInternalPromotionSelect("", {
        promotionId:
          internalPromotionAnalytics.promotionId?.toLowerCase() || undefined,
        promotionName:
          internalPromotionAnalytics.promotionName?.toLowerCase() || undefined,
        creativeName:
          internalPromotionAnalytics.creativeName?.toLowerCase() || undefined,
        creativeSlot:
          internalPromotionAnalytics.creativeSlot?.toLowerCase() || undefined,
        ...(isPrimaryButton && primaryItem ? { items: [primaryItem] } : {}),
        ...(!isPrimaryButton && secondaryItem
          ? { items: [secondaryItem] }
          : {}),
      });
    router.push(ctaUrl);
  };

  return (
    <div className={cx(st.wrapper)}>
      <div className={st.text}>
        <h3
          style={{ ...headingStyle }}
          className={cx(st.heading, st.headingSmall)}
        >
          {subHeader}
        </h3>
        <h2
          style={{ ...headingStyle }}
          className={cx(st.heading, st.headingLarge)}
        >
          {header}
        </h2>
        {disclaimer && (
          <h6 style={{ ...headingStyle }} className={cx(st.disclaimer)}>
            {disclaimer}
          </h6>
        )}
      </div>
      <div className={cx(st.buttons, isCenter && st.center)}>
        <Button
          onClick={() => onButtonClick(primaryButton.ctaUrl, true)}
          fullWidth={isMobile}
          style={{ ...primaryButtonStyle }}
          data-promotion-id={internalPromotionAnalytics?.promotionId?.toLowerCase()}
          data-promotion-name={internalPromotionAnalytics?.promotionName?.toLowerCase()}
          data-promotion-creative={internalPromotionAnalytics?.creativeName?.toLowerCase()}
          data-promotion-slot={internalPromotionAnalytics?.creativeSlot?.toLowerCase()}
          data-track-event="select_promotion"
        >
          {primaryButton.text}
        </Button>
        {secondaryButton && (
          <Button
            onClick={() => onButtonClick(secondaryButton.ctaUrl, false)}
            className={st.secondaryButton}
            fullWidth={isMobile}
            style={{ ...secondaryButtonStyle }}
            type="tertiary"
            data-promotion-id={internalPromotionAnalytics?.promotionId?.toLowerCase()}
            data-promotion-name={internalPromotionAnalytics?.promotionName?.toLowerCase()}
            data-promotion-creative={internalPromotionAnalytics?.creativeName?.toLowerCase()}
            data-promotion-slot={internalPromotionAnalytics?.creativeSlot?.toLowerCase()}
            data-track-event="select_promotion"
          >
            {secondaryButton.text}
          </Button>
        )}
      </div>
    </div>
  );
};

export default SliderTextButton;
